import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Footprints, ArrowRight, Compass, ClipboardCheck, ListChecks, FileText, Sparkles, Heart } from "lucide-react";

const fmtCount = (n) => {
  if (n == null) return "";
  if (n >= 1000) {
    const s = (n / 1000).toFixed(1).replace(".", ",");
    return (s.endsWith(",0") ? s.slice(0, -2) : s) + "k";
  }
  return String(n);
};

const FIELDS = ["Kedokteran", "Teknik", "Hukum", "Psikologi", "Sains Data", "Desain", "Ekonomi", "Sastra", "Arsitektur", "Komunikasi", "Farmasi", "Informatika"];

const STAGES = [
  { kelas: "Kelas 9 SMP", judul: "Kenali Potensi", desc: "Temukan minat & kekuatan diri sejak dini.", icon: Sparkles },
  { kelas: "Kelas 10 SMA", judul: "Jelajahi Bidang", desc: "Pilih mapel pilihan sesuai arah studimu.", icon: Compass },
  { kelas: "Kelas 11 SMA", judul: "Spesialisasi", desc: "Perdalam mapel sesuai bidang impian.", icon: ListChecks },
  { kelas: "Kelas 12 SMA", judul: "Tentukan Jurusan", desc: "Mantapkan jurusan & siapkan UTBK.", icon: FileText },
];

export default function Landing() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [parallax, setParallax] = useState(0);
  useEffect(() => { if (ready && user) nav("/dashboard"); }, [ready, user, nav]);
  useEffect(() => {
    const onScroll = () => setParallax(Math.min(window.scrollY * 0.12, 40));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [likes, setLikes] = useState(null);
  const [liked, setLiked] = useState(() => localStorage.getItem("lg_appreciated") === "1");
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/appreciate").then((r) => { if (alive) setLikes(r.data.count); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const appreciate = async () => {
    if (liked) return;
    setLiked(true);
    localStorage.setItem("lg_appreciated", "1");
    setLikes((n) => (n ?? 0) + 1);
    try { const { data } = await api.post("/appreciate"); setLikes(data.count); } catch { /* keep optimistic */ }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] grain text-[#1A1C1A]">
      <header className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/talenta-logo.png" alt="Talenta" data-testid="talenta-logo-landing" className="h-10 w-10 rounded-full object-contain shrink-0" />
          <span className="flex flex-col leading-none">
            <span className="font-heading font-black text-2xl md:text-3xl"><span className="text-[#E25E3E]">Talenta</span><span className="text-[#F4B942]">Path</span></span>
            <span className="text-[10px] font-medium text-[#9C9A94] self-end -mt-1">powered by Talenta</span>
            <span className="text-[9px] italic text-[#9C9A94] self-end -mt-0.5">Your Talent is Our Concern</span>
          </span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/hasil-simulasi" data-testid="nav-hasil-simulasi" className="text-sm font-medium text-[#1A1C1A] hover:text-[#E25E3E] transition-colors">Hasil Simulasi</Link>
          <Link to="/masuk" data-testid="header-masuk-btn" className="px-5 py-2 rounded-full bg-[#1A1C1A] text-white text-sm font-medium hover:opacity-90 transition-opacity">Masuk</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-4 pt-10 pb-16 grid md:grid-cols-12 gap-10 items-center overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#E25E3E]/25 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-24 h-72 w-72 rounded-full bg-[#4A6B53]/20 blur-3xl" />
        <div className="md:col-span-7 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-sm font-medium mb-6 animate-fade-in-up">
            <Sparkles className="h-4 w-4" /> Pemandu Arah & Progres Anak Kelas 9–12
          </div>
          <h1 className="font-heading font-black leading-[1.05] tracking-tight">
            <span className="block text-[#1A1C1A] text-2xl sm:text-3xl lg:text-4xl animate-fade-in-up" style={{ animationDelay: "80ms" }}>BECAUSE,</span>
            <span className="block text-[#D12E12] text-4xl sm:text-5xl lg:text-6xl mt-1 animate-fade-in-up" style={{ animationDelay: "200ms" }}>YOUR TALENT</span>
            <span className="block text-4xl sm:text-5xl lg:text-6xl bg-gradient-to-r from-[#D12E12] via-[#F59E0B] to-[#4A6B53] bg-clip-text text-transparent animate-fade-in-up" style={{ animationDelay: "320ms" }}>IS OUR CONCERN.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C5F5C] max-w-xl animate-fade-in-up" style={{ animationDelay: "460ms" }}>
            <b className="font-heading text-[#1A1C1A] text-lg sm:text-xl block">TalentaPath</b>
            <i className="block mt-1">Karena setiap anak punya potensi, kekuatan, dan jalan yang berbeda.</i>
            <b className="text-[#1A1C1A] block mt-3">Kami membantu anak mengenali diri, menentukan arah, dan merencanakan langkah, sementara orangtua dapat memantau progresnya menuju masa depan yang mereka impikan.</b>
          </p>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: "600ms" }}>
            <Link to="/masuk" data-testid="hero-mulai-btn" className="px-6 py-3.5 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors flex items-center gap-2 shadow-lg shadow-[#E25E3E]/25">
              Mulai Perjalanan <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#alur" className="px-6 py-3.5 rounded-full border-2 border-[#1A1C1A] font-medium hover:bg-[#1A1C1A]/5 transition-colors">Lihat Cara Kerja</a>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 animate-fade-in-up" style={{ animationDelay: "720ms" }} data-testid="appreciate-wrap">
            <button onClick={appreciate} disabled={liked} data-testid="appreciate-btn" aria-label="Beri penghargaan"
              className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-200 ${liked ? "bg-[#E25E3E] text-white shadow-lg shadow-[#E25E3E]/30 cursor-default" : "bg-white border-2 border-[#E25E3E]/40 text-[#E25E3E] hover:bg-[#E25E3E]/10 hover:border-[#E25E3E] hover:scale-105 active:scale-95"}`}>
              <Heart className={`h-5 w-5 transition-transform duration-200 ${liked ? "fill-current scale-110" : "group-hover:scale-125"}`} />
              {liked ? "Terima kasih!" : "Beri Penghargaan"}
            </button>
            {likes != null && (
              <span className="text-sm text-[#5C5F5C]" data-testid="appreciate-count">
                <b className="text-[#1A1C1A]">{fmtCount(likes)}</b> orang menyukai dan mengapresiasi ini
              </span>
            )}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4 text-sm text-[#5C5F5C]">
            <div><span className="font-heading font-black text-2xl text-[#E25E3E] block">30+</span> Asesmen</div>
            <div><span className="font-heading font-black text-2xl text-[#4A6B53] block">9+</span> Mapel</div>
            <div><span className="font-heading font-black text-2xl text-[#E25E3E] block">100+</span> Jurusan</div>
            <div><span className="font-heading font-black text-2xl text-[#4A6B53] block">80%+</span> Bidang / Karier</div>
            <div><span className="font-heading font-black text-2xl text-[#1A1C1A] block">CEQ &amp; Optimasi</span> TryOut TKA/UTBK</div>
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="clay-card overflow-hidden ring-1 ring-[#E5E0D8] shadow-xl shadow-[#1A1C1A]/5 transition-transform duration-300 ease-out will-change-transform" style={{ transform: `translateY(-${parallax}px)` }}>
            <img src="https://static.prod-images.emergentagent.com/jobs/aa68c1df-3a17-44a9-9a4d-2642a97a821f/images/68262abda7c02e324fa6e75b5ac52341a81685a7e56c7464c31a83e3e3cc2b2f.jpeg" alt="Dua siswa menatap masa depan — mengenali potensi diri" className="w-full h-[460px] object-cover object-center animate-slow-zoom" />
          </div>
          <div className="absolute -bottom-4 -left-4 clay-card px-4 py-3 bg-white flex items-center gap-2 shadow-lg">
            <span className="h-9 w-9 rounded-xl bg-[#4A6B53] text-white grid place-items-center"><Compass className="h-5 w-5" /></span>
            <div className="leading-tight">
              <p className="font-heading font-bold text-sm text-[#1A1C1A]">Arah yang jelas</p>
              <p className="text-xs text-[#5C5F5C]">dari kelas 9 sampai 12</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="py-6 border-y border-[#E5E0D8] bg-white overflow-hidden">
        <div className="marquee-track">
          {[...FIELDS, ...FIELDS].map((f, i) => (
            <span key={i} className="font-heading font-bold text-2xl text-[#1A1C1A]/70 px-8 whitespace-nowrap flex items-center gap-8">
              {f} <span className="text-[#E25E3E]">•</span>
            </span>
          ))}
        </div>
      </section>

      {/* Alur */}
      <section id="alur" className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="font-heading font-black text-3xl sm:text-4xl mb-3">Satu alur, dari kelas 9 hingga 12</h2>
        <p className="text-[#5C5F5C] max-w-2xl mb-12">Setiap anak mengikuti langkah yang sama presisinya — disesuaikan dengan jenjang kelasnya.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STAGES.map((s, i) => (
            <div key={i} className="clay-card clay-card-hover p-6" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-center justify-between mb-4">
                <span className="h-11 w-11 rounded-xl bg-[#1A1C1A] text-white grid place-items-center"><s.icon className="h-5 w-5" /></span>
                <span className="text-xs font-semibold text-[#5C5F5C]">{s.kelas}</span>
              </div>
              <h3 className="font-heading font-bold text-xl mb-1">{s.judul}</h3>
              <p className="text-sm text-[#5C5F5C]">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 clay-card p-8 grid md:grid-cols-3 gap-6 items-center bg-[#4A6B53] text-white border-[#4A6B53]">
          <div className="md:col-span-2">
            <h3 className="font-heading font-black text-2xl mb-2 flex items-center gap-2"><ClipboardCheck className="h-6 w-6" /> Penasaran dengan posisi anak saat ini?</h3>
            <p className="text-white/85">Lanjutkan dengan Asesmen Potensi Diri lengkap (karakter, kebiasaan belajar, kekuatan kognitif) dan TryOut TKA & UTBK. Semua hasil tersimpan rapi di satu aplikasi.</p>
          </div>
          <Link to="/masuk" data-testid="cta-asesmen-btn" className="justify-self-start md:justify-self-end px-6 py-3.5 rounded-full bg-white text-[#1A1C1A] font-medium hover:bg-white/90 transition-colors flex items-center gap-2">
            Coba Sekarang <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] py-8 text-center text-sm text-[#5C5F5C]">
        © {new Date().getFullYear()} TalentaPath · powered by Talenta · Pemandu potensi & karier pelajar Indonesia
      </footer>
    </div>
  );
}
